// Module imports
import { Component, OnInit, TemplateRef, Input, ContentChild, EventEmitter, Output, ElementRef, ViewChild } from "@angular/core";
import * as _ from 'lodash';
import { AuthService } from "../../services/auth.service";
import { SortPipe } from "../../pipes/sort.pipe";
import * as uuid from "uuid";

/** 
 * Component to implement Auditmap3 tables
 */
@Component({
   selector: "mapfre-auditlib-table",
   templateUrl: "./table.component.html",
   styleUrls: ["./table.component.scss"]
})
export class TableComponent implements OnInit {

   // Component variables
   @ContentChild("header", { static: false }) headerTemplate: TemplateRef<any>;
   @ContentChild("body", { static: false }) bodyTemplate: TemplateRef<any>;
   @ViewChild('inputFile', { static: false }) file: ElementRef;
   @Input() columns;
   @Input() actions;
   @Input() buttons;
   @Input() createButton;
   @Input() filterButton;
   @Input() exportButton;
   @Input() importButton;
   @Input() sorted = true;
   @Input() collapsed = false;
   @Input() sortField;

   /**
    * Defult actions of the table
    */
   _defaultActions = {
      edit: true,
      delete: true
   };

   /**
    * Define and init headers
    */
   _header
   @Input()
   get header() {
      return this._header
   }

   set header(value) {
      this._header = this.initHeaders(value)
   }

   /**
    * Crate body
    */
   _body;
   @Input()
   get body() {
      return this._body;
   }
   set body(value) {
      /** Workaround we find the id property name and we order desc unless we find edit property cause is creation mode */
      if (value && value.length > 0) {
         if (!_.hasIn(value[0], 'edit') && this.sorted) {

            // Sort by field if exists
            if (this.sortField && this.sortField!='order') {
               this._body = _.orderBy(value, [this.sortField], 'desc');
            }
            if(this.sortField==='order'){ // feature/038041 - orden -- 1 por orden 2 por seleccionados 3 fullname 
               this._body = _.orderBy(value, [this.sortField], 'asc');
            }
            else {
               const id = _.find(_.keysIn(value[0]), (prop) => { return _.findIndex(prop, 'id') });
               this._body = _.orderBy(value, id, 'desc');
            }
         }
         else {
            this._body = value;
         }

      } else {
         this._body = [];
      }

   }

   /**
    * Set default actions
    */
   @Input()
   set defaultActions(newObject) {
      this._defaultActions = newObject;
      this._defaultActions = _.merge({ delete: true, edit: true }, this._defaultActions);
   }
   get defaultActions() {
      return this._defaultActions;
   }

   /**
    * Select data when change it
    */
   _selectData;
   @Input()
   get selectData() {
      return this._selectData;
   }
   set selectData(value) {
      this._selectData = value;
      setTimeout(() => this.selectDataChange.emit(this._selectData));
   }

   // Default rows
   @Input() rowsPerPage = 10;

   @Input() defaultData = {};

   // Variables
   @Input() selectTable;
   @Input() typeSelect;
   @Input() defaultValidators = [];
   @Output() selectDataChange = new EventEmitter();

   @Output() actionClicked = new EventEmitter();
   @Output() buttonClicked = new EventEmitter();
   @Output() updated = new EventEmitter();
   @Output() created = new EventEmitter();
   @Output() deleted = new EventEmitter();
   @Output() selectChange = new EventEmitter();
   @Output() filteredDataChange = new EventEmitter();
   bodyFiltered;
   bodyPaginated;
   sortConfig;
   loading: boolean = false;
   editMode: boolean = false;
   @Input() filterMode: boolean = false;
   nuuma: any;

   /**
    * Component constructor
    * @param authService 
    * @param sortPipe 
    */
   constructor(private authService: AuthService, private sortPipe: SortPipe) { }

   /** 
    * Component init
    */
   ngOnInit() {
      this.nuuma = this.authService.getNuuma()
   }

   /** 
    * Create table
    */
   create() {
      
      let objeto = { uuid: uuid.v4(), edit: true, USER_INSERT: this.nuuma };
      _.forEach(_.keys(this.defaultData), key => {
         objeto[key] = this.defaultData[key];
      })

      this.body.unshift(objeto);
      this.body = [...this.body];
      this.buttonClicked.emit('create');
   }

   /**
    * Click on action of the table
    * @param action 
    * @param item 
    */
   onActionClicked(action, item) {
      const index = _.findIndex(this.body, elem => {
         return _.isEqual(elem, item);
      });
      if (action === "cancelled") {
         _.pullAt(this.body, index);
         this.body = [...this.body];
      } else {
         this.actionClicked.emit({ action, item, index });
      }
   }

   /** 
    * Import excel
    */
   importExcel() {
      this.file.nativeElement.value = null;
      this.file.nativeElement.click();
   }

   /**
    * Click on button
    * @param button 
    */
   onButtonClicked(button) {
      this.buttonClicked.emit(button.key);
   }

   /**
    * Save form
    * @param formValue 
    * @param item 
    */
   onSave(formValue, item) {
      const index = _.findIndex(this.body, elem => {
         return _.isEqual(elem, item);
      });
      this.body[index] = _.assign(item, formValue);
      this.updated.emit(this.body[index]);
   }

   /**
    * On create item action
    * @param formValue 
    * @param item 
    */
   onCreate(formValue, item) {
      const index = _.findIndex(this.body, (elem: any) => {
         return elem.uuid === item.uuid;
      });
      item.edit = false;
      this.body[index] = _.merge(item, formValue);
      this.created.emit(this.body[index]);
   }

   /**
    * On delete item action
    * @param item 
    */
   onDelete(item) {
      const index = _.findIndex(this.body, elem => {
         return _.isEqual(elem, item);
      });
      this.deleted.emit({ item, index });
   }

   /**
    * Filter data
    * @param filteredData 
    */
   onFilteredDataChange(filteredData) {
      this.loading = true;
      this.bodyFiltered = filteredData;
      this.filteredDataChange.emit(filteredData);
      setTimeout(() => {
         this.loading = false;
      });
   }

   /**
    * On select item action
    * @param event 
    */
   onSelectChange(event) {
      this.selectChange.emit(event);
   }

   /**
    * On header click action
    * @param col 
    */
   onHeaderClick(col) {
      if (col.sortable) {
         let sortConfig = {
            key: col.field,
            subkey: _.get(col, 'options.field'),
            order: this.sortConfig ? -this.sortConfig.order : 1
         };
         this.sortConfig = col.sortable ? sortConfig : null;
         this.sort();
      }
   }

   /** 
    * Sort table, by default, with id of data. Specify sortField to use another
    */
   sort() {
      this.bodyFiltered = this.sortConfig ? this.sortPipe.transform(this.bodyFiltered, this.sortConfig) : this.bodyFiltered;
   }

   /**
    * Init headers
    * @param headers 
    */
   initHeaders(headers) {

      // Set sortable true by default
      _.forEach(headers, function (header) {
         header.sortable = header.sortable != null ? header.sortable : true
      })

      return headers;
   }
}
